import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FormField from '../components/FormField';
import { motion } from 'framer-motion';

// Using the standardized FormField component

const PriorVisitFormPage = () => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    Name: '',
    Gender: '',
    Age: '',
    MedHis: '',
    FamMH: '',
    Allerg: '',
    Medications: '',
    Supp: '',
    Diet: '',
    DietRestr: '',
    Sleep: '',
    MenHealth: '',
    Occup: '',
    VacHis: '',
    Exerc: '',
    Preg: '',
    RTravHist: '',
    Alc: '',
    Smok: '',
    IlDrug: '',
    InsCov: '',
    FamDoc: '',
    CAC: '',
    CUR: '',
    ChiefC: '',
    DocSpec: '',
    DocSpecOther: ''
  });


  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));

    // Clear error when field is changed
    if (errors[id]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.Name.trim()) newErrors.Name = "Name is required";
    if (!formData.Gender) newErrors.Gender = "Gender is required";
    if (!formData.Age) {
      newErrors.Age = "Age is required";
    } else {
      const age = Number(formData.Age);
      if (!Number.isInteger(age) || age < 0 || age > 120) {
        newErrors.Age = "Please enter a valid age in years (0-120)";
      }
    }

    // Word count validation for MedHis (300 words)
    if (formData.MedHis && formData.MedHis.trim().split(/\s+/).length > 300) {
      newErrors.MedHis = "Medical history must be 300 words or less";
    }

    // Character count validation
    if (formData.DietRestr && formData.DietRestr.length > 200) {
      newErrors.DietRestr = "Dietary restrictions must be 200 characters or less";
    }

    if (formData.Occup && formData.Occup.length > 150) {
      newErrors.Occup = "Occupation must be 150 characters or less";
    }

    if (formData.RTravHist && formData.RTravHist.length > 150) {
      newErrors.RTravHist = "Recent travel history must be 150 characters or less";
    }

    if (formData.CAC === "Yes" && formData.CUR && formData.CUR.trim().split(/\s+/).length > 150) {
      newErrors.CUR = "Checkup result must be 150 words or less";
    }

    if (formData.DocSpec === 'Other' && !formData.DocSpecOther.trim()) {
      newErrors.DocSpecOther = "Please enter the doctor's specialty";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Unguessable session ID -- it doubles as the key for the locally
      // stored session history, so it must not be predictable.
      const sessionId = `session_${(window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`}`;

      // Fold the free-text "Other" specialty into DocSpec so the AI and the
      // report see the real specialty.
      const { DocSpecOther, ...rest } = formData;
      const submitData = {
        ...rest,
        DocSpec: formData.DocSpec === 'Other' && DocSpecOther.trim()
          ? DocSpecOther.trim()
          : formData.DocSpec
      };

      // Store form data in localStorage so a page refresh doesn't lose it
      localStorage.setItem('formData', JSON.stringify({ ...submitData, sessionId }));

      // No backend call needed here: the backend is stateless and receives
      // the form data with every chat request.
      navigate('/priorv-chat', {
        state: {
          formData: { ...submitData, sessionId },
          sessionId
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <h1 className="text-3xl font-bold text-center pt-4 text-gray-900 mb-3 font-serif">
            <span className="text-primary">Patients Empowered</span> Patient Information Form
          </h1>
          <p className="text-center text-gray-600 mb-2 max-w-2xl mx-auto">
            Complete this form to help Patients Empowered gather all necessary information for your upcoming doctor visit.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border border-gray-200"
          >
            <div className="flex items-center mb-6 pb-4 border-b border-gray-100">
              <div className="bg-primary bg-opacity-10 p-3 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl pt-3.5 font-semibold text-gray-800">Health Information Form</h2>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="p-6 bg-gradient-to-r from-primary-light/30 to-white border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">Please fill out this form to help us prepare you for your visit</h2>
                <p className="text-gray-600">Your information helps us provide better care during your appointment</p>
              </div>

              {/* Basic Information Section */}
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 relative flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  Basic Information
                  <div className="absolute bottom-0 left-0 w-24 h-1 bg-primary rounded-sm"></div>
                </h2>
                <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-4">
                  <FormField
                    label="Name (or nickname)"
                    id="Name"
                    type="text"
                    value={formData.Name}
                    onChange={handleChange}
                    required
                    error={errors.Name}
                    placeholder="Enter your name"
                  />
                  <FormField
                    label="Gender"
                    id="Gender"
                    type="dropdown"
                    value={formData.Gender}
                    onChange={handleChange}
                    options={['Male', 'Female', 'Non-binary', 'Prefer not to say']}
                    required
                    error={errors.Gender}
                    placeholder="Select gender"
                  />
                  <FormField
                    label="Age (years)"
                    id="Age"
                    type="number"
                    value={formData.Age}
                    onChange={handleChange}
                    required
                    error={errors.Age}
                    placeholder="Enter age in years"
                  />
                  
                </div>

               </div> 
                

                {/* Medical Information Section */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 relative flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6zm4 4a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1H8a1 1 0 110-2h1v-1a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                  Medical Information
                  <div className="absolute bottom-0 left-0 w-24 h-1 bg-primary rounded-sm"></div>
                </h2>
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-2 md:gap-2">
                    <FormField
                      label="Medical History"
                      id="MedHis"
                      type="textarea"
                      value={formData.MedHis}
                      onChange={handleChange}
                      error={errors.MedHis}
                      placeholder="Please describe any significant medical conditions, surgeries, or ongoing health issues"
                    />
                    <FormField
                      label="Family Medical History"
                      id="FamMH"
                      type="textarea"
                      value={formData.FamMH}
                      onChange={handleChange}
                      placeholder="Any hereditary conditions or significant family health issues"
                    />
                    <FormField
                      label="Allergies"
                      id="Allerg"
                      type="textarea"
                      value={formData.Allerg}
                      onChange={handleChange}
                      placeholder="List any allergies to medications, foods, or environmental factors"
                    />
                    <FormField
                      label="Current Medication, Dose, Frequency"
                      id="Medications"
                      type="textarea"
                      value={formData.Medications}
                      onChange={handleChange}
                      placeholder="List all current medications with dosage and frequency"
                    />
                  </div>
                </div>

                {/* Lifestyle Information Section */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 relative flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                </svg>
                  Lifestyle Information
                  <div className="absolute bottom-0 left-0 w-24 h-1 bg-primary rounded-sm"></div>
                </h2>
                  <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-4">
                    <FormField
                      label="Diet"
                      id="Diet"
                      type="dropdown"
                      value={formData.Diet}
                      onChange={handleChange}
                      options={['Excellent', 'Good', 'Can Improve', 'Low Quality']}
                    />

                    <FormField
                      label="Dietary Restriction"
                      id="DietRestr"
                      type="text"
                      value={formData.DietRestr}
                      onChange={handleChange}
                      placeholder="List items you avoid"
                      error={errors.DietRestr}
                    />
                    <FormField
                      label="Supplements"
                      id="Supp"
                      type="text"
                      value={formData.Supp}
                      onChange={handleChange}
                      placeholder="List supplements you take"
                    />                    
                    <FormField
                      label="Sleep"
                      id="Sleep"
                      type="dropdown"
                      value={formData.Sleep}
                      onChange={handleChange}
                      options={['Excellent', 'Good', 'Can Improve', 'Low Quality']}
                    />
                    <FormField
                      label="Exercise"
                      id="Exerc"
                      type="dropdown"
                      value={formData.Exerc}
                      onChange={handleChange}
                      options={['Excellent', 'Good', 'Few times a month', 'No Exercise']}
                    />
                    <FormField
                      label="Self Assessed Mental Health Status"
                      id="MenHealth"
                      type="dropdown"
                      value={formData.MenHealth}
                      onChange={handleChange}
                      options={['Excellent', 'Good', 'Can Improve', 'Need Support']}
                    />
                    <FormField
                      label="Occupation"
                      id="Occup"
                      type="text"
                      value={formData.Occup}
                      onChange={handleChange}
                      error={errors.Occup}
                      placeholder="Current and past occupations"
                    />

                    <FormField
                      label="Recent Travel History"
                      id="RTravHist"
                      type="text"
                      value={formData.RTravHist}
                      onChange={handleChange}
                      error={errors.RTravHist}
                      placeholder="List recent travel destinations"
                    />
                    <FormField
                      label="Vaccination History"
                      id="VacHis"
                      type="dropdown"
                      value={formData.VacHis}
                      onChange={handleChange}
                      options={['Have taken all vaccines', 'Had most vaccines', 'Had only a few vaccines', 'No vaccination']}
                    />                    
                    <FormField
                      label="Alcohol Consumption"
                      id="Alc"
                      type="dropdown"
                      value={formData.Alc}
                      onChange={handleChange}
                      options={[
                        'Heavy drinking (4 or more drinks on any day)',
                        'Moderate drinking (2 drinks or less in a day)',
                        'Social drinking (1 or 2 drinks a week)',
                        'No alcohol consumption'
                      ]}
                    />
                    <FormField
                      label="Recreational Drug"
                      id="IlDrug"
                      type="text"
                      value={formData.IlDrug}
                      onChange={handleChange}
                      placeholder="List any recreational drugs use"
                    />
                    <FormField
                      label="Smoking/Nicotine"
                      id="Smok"
                      type="dropdown"
                      value={formData.Smok}
                      onChange={handleChange}
                      options={[
                        'Heavy smoker (25 or more cigarettes per day)',
                        'Moderate smoker (15 to 24 cigarettes per day)',
                        'Light smoker (1 to 14 cigarettes per day)',
                        'Vaping Nicotine',
                        'Nicotine pouch user',
                        'Non-Smoker'
                      ]}
                    />
                    <FormField
                      label="Pregnancy Status/History"
                      id="Preg"
                      type="dropdown"
                      value={formData.Preg}
                      onChange={handleChange}
                      options={['Male (Not Applicable)', 'Pregnant', 'Recently delivered', 'Previously pregnant', 'Never been pregnant']}
                    />
                  </div>

                </div>


              {/* Healthcare Access Section */}
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 relative flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm5 4a1 1 0 011 1v2h2a1 1 0 110 2h-2v2a1 1 0 11-2 0v-2H7a1 1 0 110-2h2V8a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                  Healthcare Access Information
                  <div className="absolute bottom-0 left-0 w-24 h-1 bg-primary rounded-sm"></div>
                </h2>
                <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-4">
                  <FormField
                    label="Health Insurance Coverage"
                    id="InsCov"
                    type="dropdown"
                    value={formData.InsCov}
                    onChange={handleChange}
                    options={['Full coverage (private or public)', 'Partial coverage', 'No coverage']}
                  />
                  <FormField
                    label="Do you have a family doctor?"
                    id="FamDoc"
                    type="dropdown"
                    value={formData.FamDoc}
                    onChange={handleChange}
                    options={['Yes', 'No', 'Looking for one']}
                  />
                  <FormField
                    label="Any annual checkup recently?"
                    id="CAC"
                    type="dropdown"
                    value={formData.CAC}
                    onChange={handleChange}
                    options={['Yes', 'No']}
                  />
                </div>

                {formData.CAC === 'Yes' && (
                  <div className="grid grid-cols-1 gap-4 md:gap-6 mb-6">
                    <div>
                      <FormField
                        label="What was the result of check up?"
                        id="CUR"
                        type="textarea"
                        value={formData.CUR}
                        onChange={handleChange}
                        error={errors.CUR}
                        placeholder="Describe the result of your checkup"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Current Visit Information */}
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 relative flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Current Visit Information
                  <div className="absolute bottom-0 left-0 w-24 h-1 bg-primary rounded-sm"></div>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-6 mb-6">
                  <div>
                    <FormField
                      label="The reason for your upcoming doctor visit"
                      id="ChiefC"
                      type="text"
                      value={formData.ChiefC}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <FormField
                      label="The doctor's specialty"
                      id="DocSpec"
                      type="dropdown"
                      value={formData.DocSpec}
                      onChange={handleChange}
                      options={[
                        'Family doctor/General practitioner',
                        'Cardiologist',
                        'Oncologist',
                        'Dermatologist',
                        'Gastroenterologist',
                        'Neurologist',
                        'Orthopedic surgeon',
                        'Psychiatrist',
                        'Gynecologist',
                        'Urologist',
                        'Pediatrician',
                        'Other'
                      ]}
                    />
                  </div>
                  
                  {formData.DocSpec === 'Other' && (
                  <div className="grid grid-cols-1 gap-4 md:gap-6 mb-6">
                    <div>
                      <FormField
                        label="Please enter the specialty."
                        id="DocSpecOther"
                        type="text"
                        value={formData.DocSpecOther}
                        onChange={handleChange}
                        error={errors.DocSpecOther}
                        placeholder="e.g. Endocrinologist"
                      />
                    </div>
                  </div>
                )}
                </div>
              </div>

              {/* Submit button */}
              <div className="text-center mt-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="px-8 py-4 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-4 focus:ring-primary focus:ring-opacity-30 transition-all duration-200 shadow-lg inline-flex items-center space-x-2"
                  disabled={Object.keys(errors).length > 0}
                >
                  <span>Start Preparing with Patients Empowered</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </motion.button>
                <p className="mt-4 text-sm text-gray-600">Your information stays on this device — it is only sent to the AI models to answer your questions, and is never stored on our servers.</p>
              </div>
            </form>
          </motion.div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default PriorVisitFormPage;